(() => {
  'use strict';

  const config = window.MEGETIZE_SEALER_CART || {};
  const variants = Array.isArray(config.variants) ? config.variants : [];
  const bundleQuantity = { single: 1, double: 2, triple: 3 };
  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const route = (path) => `${routesRoot}${String(path || '').replace(/^\//, '')}`;

  let cartCount = 0;
  let requestInFlight = false;

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const variantText = (variant) => normalize([
    variant && variant.title,
    ...(Array.isArray(variant && variant.options) ? variant.options : [])
  ].join(' '));

  const isAvailable = (variant) => variant && variant.available !== false;

  function findBundleVariant(bundleId) {
    const normalizedBundle = bundleQuantity[bundleId] ? bundleId : 'double';
    const requestedQuantity = bundleQuantity[normalizedBundle];
    const availableVariants = variants.filter(isAvailable);
    const pool = availableVariants.length ? availableVariants : variants;

    const aliases = {
      single: ['single', '1 pack', 'pack of 1', '1x', 'one'],
      double: ['double', '2 pack', 'pack of 2', '2x', 'two'],
      triple: ['triple', '3 pack', 'pack of 3', '3x', 'three']
    };

    const exact = pool.find((variant) => aliases[normalizedBundle].some((alias) => variantText(variant).includes(alias)));
    if (exact) return { variant: exact, quantity: 1, bundleId: normalizedBundle };

    // When Shopify has a separate variant for every bundle, keep the visual
    // Single / Double / Triple order used by the storefront.
    if (pool.length >= 3) {
      const index = normalizedBundle === 'single' ? 0 : normalizedBundle === 'double' ? 1 : 2;
      return { variant: pool[index] || pool[0], quantity: 1, bundleId: normalizedBundle };
    }

    // When the product has one base variant, represent bundles as quantity.
    return { variant: pool[0], quantity: requestedQuantity, bundleId: normalizedBundle };
  }

  function inferBundle(button, originalText) {
    const buttonText = normalize(originalText || (button && button.textContent));
    if (buttonText.includes('single')) return 'single';
    if (buttonText.includes('triple')) return 'triple';
    if (buttonText.includes('double')) return 'double';

    const section = button && button.closest('section, main, article, [class*="grid"]');
    const selected = (section && section.querySelector('button[aria-pressed="true"]')) ||
      document.querySelector('button[aria-pressed="true"]');
    const selectedText = normalize(selected && selected.textContent);
    if (selectedText.includes('single')) return 'single';
    if (selectedText.includes('triple')) return 'triple';
    if (selectedText.includes('double')) return 'double';
    return 'double';
  }

  function ensureToast() {
    let toast = document.querySelector('[data-shopify-cart-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.dataset.shopifyCartToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = '<span data-toast-icon aria-hidden="true">✓</span><div><b data-toast-title>Added to cart</b><small data-toast-copy>Your Shopify cart is updated.</small></div><a href="' + route('cart') + '">View cart</a>';
    document.body.appendChild(toast);
    return toast;
  }

  function showToast(message, type = 'success') {
    const toast = ensureToast();
    const title = toast.querySelector('[data-toast-title]');
    const copy = toast.querySelector('[data-toast-copy]');
    const icon = toast.querySelector('[data-toast-icon]');

    toast.classList.toggle('is-error', type === 'error');
    if (title) title.textContent = type === 'error' ? 'Cart update failed' : 'Added to cart';
    if (icon) icon.textContent = type === 'error' ? '!' : '✓';
    if (copy) copy.textContent = message || (type === 'error' ? 'Please try again.' : 'Your Shopify cart is updated.');

    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 3800);
  }

  function ensureStyles() {
    if (document.getElementById('megetize-cart-bridge-styles')) return;
    const style = document.createElement('style');
    style.id = 'megetize-cart-bridge-styles';
    style.textContent = `
      [data-shopify-cart-toast]{position:fixed;right:22px;bottom:22px;z-index:9999;display:flex;align-items:center;gap:12px;max-width:min(390px,calc(100vw - 28px));padding:13px 15px;border:1px solid rgba(174,145,122,.34);border-radius:18px;background:#fffaf2;color:#2b2420;box-shadow:0 22px 60px rgba(55,38,25,.2);opacity:0;transform:translateY(14px);pointer-events:none;transition:.24s ease}
      [data-shopify-cart-toast].is-visible{opacity:1;transform:none;pointer-events:auto}
      [data-shopify-cart-toast]>span{display:grid;width:34px;height:34px;place-items:center;border-radius:50%;background:#76926f;color:white;font-weight:900}
      [data-shopify-cart-toast].is-error>span{background:#b64f3b}
      [data-shopify-cart-toast] div{display:grid;gap:1px;min-width:0;flex:1}
      [data-shopify-cart-toast] b{font:700 14px/1.2 ui-sans-serif,system-ui,sans-serif}
      [data-shopify-cart-toast] small{overflow:hidden;color:#756960;font:500 12px/1.35 ui-sans-serif,system-ui,sans-serif;text-overflow:ellipsis;white-space:nowrap}
      [data-shopify-cart-toast] a{color:#db6741;font:800 12px/1 ui-sans-serif,system-ui,sans-serif;text-decoration:none;white-space:nowrap}
      @media(max-width:620px){[data-shopify-cart-toast]{left:14px;right:14px;bottom:14px}}
    `;
    document.head.appendChild(style);
  }

  function updateHeaderCount(count) {
    cartCount = Math.max(0, Number(count || 0));
    document.querySelectorAll('button[aria-label^="Open cart"], a[aria-label^="Open cart"]').forEach((control) => {
      const nextLabel = `Open cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`;
      if (control.getAttribute('aria-label') !== nextLabel) control.setAttribute('aria-label', nextLabel);

      let badge = control.querySelector('[data-shopify-cart-count]');
      const existing = [...control.querySelectorAll('span')].find((span) =>
        /absolute/.test(span.className || '') && /^\d+$/.test((span.textContent || '').trim())
      );
      if (!badge && existing) {
        badge = existing;
        badge.dataset.shopifyCartCount = '';
      }

      if (cartCount > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.dataset.shopifyCartCount = '';
          badge.className = 'absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-coral px-1 text-[11px] font-bold text-primary-foreground';
          control.appendChild(badge);
        }
        if (badge.textContent !== String(cartCount)) badge.textContent = String(cartCount);
        if (badge.hidden) badge.hidden = false;
      } else if (badge && !badge.hidden) {
        badge.hidden = true;
      }
    });
  }

  async function getCart() {
    const response = await fetch(route('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  }

  async function refreshCartCount() {
    try {
      const cart = await getCart();
      updateHeaderCount(cart.item_count);
      return cart;
    } catch (_) {
      return null;
    }
  }

  async function addBundle(bundleId, multiplier = 1, options = {}) {
    const resolved = findBundleVariant(bundleId);
    if (!resolved.variant || !resolved.variant.id) {
      throw new Error('The Mini Sealer Shopify variant is not connected. Confirm the product handle and publish it to Online Store.');
    }
    if (resolved.variant.available === false) throw new Error('This option is currently sold out.');

    const quantity = Math.max(1, Number(resolved.quantity || 1) * Math.max(1, Number(multiplier || 1)));
    const item = {
      id: Number(resolved.variant.id),
      quantity
    };

    // Preserve the selected pack name when Shopify uses separate bundle variants.
    if (variants.length >= 3) {
      item.properties = { Pack: resolved.bundleId.charAt(0).toUpperCase() + resolved.bundleId.slice(1) };
    }

    const response = await fetch(route('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    });

    if (!response.ok) {
      let message = 'Unable to add this item right now.';
      try {
        const error = await response.json();
        message = error.description || error.message || message;
      } catch (_) {}
      throw new Error(message);
    }

    const cart = await getCart();
    updateHeaderCount(cart.item_count);
    document.dispatchEvent(new CustomEvent('megetize:cart-updated', { detail: cart }));

    if (options.checkout) {
      window.location.assign(route('checkout'));
    } else if (options.redirectToCart) {
      window.location.assign(route('cart'));
    } else {
      const packName = resolved.bundleId.charAt(0).toUpperCase() + resolved.bundleId.slice(1);
      showToast(`${packName} pack added. ${cart.item_count} item${cart.item_count === 1 ? '' : 's'} in your shared cart.`);
    }
    return cart;
  }

  window.MegetizeShopifyCart = { addBundle, refresh: refreshCartCount, getCart };

  ensureStyles();

  document.addEventListener('DOMContentLoaded', () => {
    refreshCartCount();
    const observer = new MutationObserver(() => updateHeaderCount(cartCount));
    observer.observe(document.body, { childList: true, subtree: true });
  });

  window.addEventListener('pageshow', refreshCartCount);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshCartCount();
  });

  document.addEventListener('click', async (event) => {
    const control = event.target.closest('button, a');
    if (!control) return;

    if ((control.getAttribute('aria-label') || '').startsWith('Open cart')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(route('cart'));
      return;
    }

    if (control.tagName !== 'BUTTON') return;
    const originalText = control.textContent || '';
    const normalizedText = normalize(originalText);
    const isBuyNow = normalizedText === 'buy it now' || normalizedText === 'buy now';
    const isAdd = /^add to cart\b/.test(normalizedText) || /^add (single|double|triple)\b/.test(normalizedText);
    if (!isBuyNow && !isAdd) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (control.disabled || requestInFlight) return;

    const bundleId = inferBundle(control, originalText);
    const originalMarkup = control.innerHTML;
    requestInFlight = true;
    control.disabled = true;
    control.textContent = isBuyNow ? 'Preparing checkout…' : 'Adding…';

    try {
      await addBundle(bundleId, 1, { checkout: isBuyNow });
    } catch (error) {
      showToast(error.message || 'Unable to update your cart.', 'error');
      control.innerHTML = originalMarkup;
      control.disabled = false;
    } finally {
      requestInFlight = false;
      if (!isBuyNow && document.contains(control)) {
        control.innerHTML = originalMarkup;
        control.disabled = false;
      }
    }
  }, true);
})();
